import { useState } from 'react';
import { Link } from '@tanstack/react-router';
import { Check, Copy, Sparkles } from 'lucide-react';
import { Button } from '@jeraisonne/ui';
import { buildSeriesInstructions } from '@jeraisonne/db/visio';
import { useAuth } from '@/lib/auth';
import { useGenerateRendezVousSummary, useRendezVousSummary } from '@/lib/tutor/rendezvous';
import {
  useMyVisioSeries,
  usePreferredSeriesId,
  useSeedDefaultVisioSeries,
} from '@/lib/tutor/visio-series';
import { copySummaryToClipboard } from '@/lib/visio/copy-summary';
import { SummaryMarkdown } from './summary-markdown';

const GENERATED_AT = new Intl.DateTimeFormat('fr-FR', {
  timeZone: 'Europe/Paris',
  day: 'numeric',
  month: 'long',
  hour: '2-digit',
  minute: '2-digit',
});

/** Copie le résumé (texte propre + HTML riche) ; le libellé confirme le résultat 2 s. */
function CopySummaryButton({ markdown }: { markdown: string }) {
  const [state, setState] = useState<'idle' | 'copied' | 'failed'>('idle');

  async function handleCopy() {
    setState((await copySummaryToClipboard(markdown)) ? 'copied' : 'failed');
    setTimeout(() => setState('idle'), 2000);
  }

  return (
    <button
      type="button"
      onClick={() => void handleCopy()}
      className="border-ink-200 text-ink-700 hover:bg-ink-50 inline-flex min-h-[2.75rem] items-center gap-1.5 rounded-xl border px-3 text-xs font-semibold transition-colors active:scale-95"
    >
      {state === 'copied' ? <Check className="size-3.5" /> : <Copy className="size-3.5" />}
      {state === 'copied' ? 'Copié' : state === 'failed' ? 'Copie impossible' : 'Copier'}
    </button>
  );
}

/**
 * Résumé du rendez-vous — TUTEUR seul. Le tuteur choisit une SÉRIE de points
 * (sa propre liste, gérée dans Rendez-vous → Séries) : le résumé répond à chaque
 * point à partir du dialogue. La série est modifiable à chaque génération.
 * Série proposée par défaut : celle du résumé déjà généré, sinon celle choisie
 * PENDANT la visio, sinon celle choisie à la CRÉATION du rendez-vous, sinon la
 * première de la liste.
 */
export function RendezVousSummaryPanel({
  reservationId,
  messageCount,
}: {
  reservationId: string;
  messageCount: number;
}) {
  const { user } = useAuth();
  const { data: seriesList = [], isLoading: seriesLoading } = useMyVisioSeries(user?.id);
  const { data: summary } = useRendezVousSummary(reservationId);
  const { data: roomSeriesId } = usePreferredSeriesId(reservationId);
  const generate = useGenerateRendezVousSummary(reservationId);
  const seed = useSeedDefaultVisioSeries(user?.id);
  const [pickedId, setPickedId] = useState<string | null>(null);

  const preferredId = pickedId ?? summary?.seriesId ?? roomSeriesId ?? null;
  const selected = seriesList.find((s) => s.id === preferredId) ?? seriesList[0] ?? null;
  const canGenerate = messageCount > 0 && !!selected && !generate.isPending;

  function handleGenerate() {
    if (!selected) return;
    generate.mutate({ instructions: buildSeriesInstructions(selected), seriesId: selected.id });
  }

  return (
    <section className="border-ink-100 space-y-4 rounded-2xl border bg-white p-4 text-left">
      <div>
        <h2 className="text-ink-900 text-sm font-bold">Résumé du rendez-vous</h2>
        <p className="text-ink-500 mt-1 text-xs">
          Choisis la série de points à traiter : le résumé répond à chacun à partir du dialogue.
          Seul toi vois ce résumé.
        </p>
      </div>

      {seriesLoading ? (
        <p className="text-ink-500 text-sm">Chargement de tes séries…</p>
      ) : seriesList.length === 0 ? (
        <div className="border-ink-100 bg-ink-50 rounded-xl border p-4 text-center">
          <p className="text-ink-700 text-sm font-semibold">Tu n’as pas encore de série</p>
          <p className="text-ink-500 mt-1 text-xs">
            Pars de nos modèles ou crée la tienne dans Rendez-vous → Séries.
          </p>
          <div className="mt-3 flex flex-col gap-2 sm:flex-row sm:justify-center">
            <Button
              onClick={() => seed.mutate()}
              loading={seed.isPending}
              className="min-h-[2.75rem] w-full active:scale-[0.98] sm:w-auto"
            >
              Ajouter les séries de départ
            </Button>
            <Link
              to="/tutor/rendez-vous/series"
              className="border-ink-200 text-ink-700 flex min-h-[2.75rem] items-center justify-center rounded-xl border px-3 text-sm font-semibold active:scale-[0.98]"
            >
              Créer une série
            </Link>
          </div>
          {seed.isError && (
            <p role="alert" className="text-accent-700 mt-2 text-xs font-medium">
              {seed.error.message}
            </p>
          )}
        </div>
      ) : (
        <div>
          <div className="flex items-end justify-between gap-2">
            <label htmlFor="rdv-summary-series" className="text-ink-700 text-xs font-semibold">
              Série de points
            </label>
            <Link
              to="/tutor/rendez-vous/series"
              className="text-primary-600 flex min-h-[2.75rem] items-center text-xs font-semibold underline"
            >
              Gérer mes séries
            </Link>
          </div>
          <select
            id="rdv-summary-series"
            value={selected?.id ?? ''}
            onChange={(e) => setPickedId(e.target.value)}
            className="border-ink-200 text-ink-900 focus:border-primary-500 focus:ring-primary-500/20 min-h-[2.75rem] w-full rounded-xl border bg-white px-3 text-base focus:outline-none focus:ring-2 sm:text-sm"
          >
            {seriesList.map((s) => (
              <option key={s.id} value={s.id}>
                {s.title}
              </option>
            ))}
          </select>

          {selected && (
            <details className="mt-2">
              <summary className="text-ink-500 min-h-[2.75rem] cursor-pointer py-2.5 text-xs font-semibold">
                Voir les {selected.points.length} points de la série
              </summary>
              <ol className="text-ink-700 ml-5 list-decimal space-y-1 pb-1 text-sm">
                {selected.points.map((point) => (
                  <li key={point.id}>{point.text}</li>
                ))}
              </ol>
            </details>
          )}
        </div>
      )}

      {seriesList.length > 0 && (
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
          <Button
            onClick={handleGenerate}
            disabled={!canGenerate}
            loading={generate.isPending}
            className="min-h-[2.75rem] w-full active:scale-[0.98] sm:w-auto"
          >
            <Sparkles className="size-4" />
            {summary ? 'Régénérer le résumé' : 'Générer le résumé'}
          </Button>
          {messageCount === 0 && (
            <p className="text-ink-500 text-xs">Le chat est vide : il n’y a rien à résumer.</p>
          )}
        </div>
      )}

      {generate.isError && (
        <p
          role="alert"
          className="border-accent-200 bg-accent-50 text-accent-700 rounded-xl border px-3 py-2 text-xs font-medium"
        >
          {generate.error.message}
        </p>
      )}

      {summary && (
        <div className="border-ink-100 border-t pt-4">
          <div className="mb-2 flex items-center justify-between gap-2">
            <p className="text-ink-400 min-w-0 text-xs">
              Généré le {GENERATED_AT.format(new Date(summary.generatedAt))}
              {summary.seriesTitle && ` · Série « ${summary.seriesTitle} »`}
            </p>
            <CopySummaryButton markdown={summary.summaryMd} />
          </div>
          <SummaryMarkdown markdown={summary.summaryMd} />
        </div>
      )}
    </section>
  );
}
